/************************************************************************************
 * 
 * 
 * 
 *  DATA TEXT METHODS
 * 
 * 
 * ************************************************************************************/
import * as jsPdf from 'jspdf';
import * as moment from 'moment-timezone';
import { EInputMask } from 'src/Components/EInput';
import { IDrillBit, ITest, ITestContact } from 'src/Interfaces/TestInterface';
import { IEngineer, IWell } from 'src/Interfaces/WellInterface';
import DisplayRounding from 'src/Services/DisplayRounding';
import CalcStore from 'src/Stores/Calc/CalcStore';
import { textArrayInBoxes, textCenteredInBox, textLeftJustifiedInBox } from '../PopulateDataMethods';
import * as MM from './SpecMilsMud';



const dr = DisplayRounding;
const dataMask = new EInputMask();


export const populateHeader = (test: ITest, well: IWell, logoBlob: HTMLImageElement, mudReport: jsPdf) => {
    // HEADER TEXT
    mudReport.setFontSize(10);
    mudReport.setFontStyle('bold');
    let tempTextArr: any = [
        ['REPORT'],
        ['SPUD DATE'],
        ['ACTIVITY'],
    ];
    textArrayInBoxes(tempTextArr, MM.PAGE_BORDER_WIDTH, MM.PAGE_BORDER_WIDTH, MM.ONE_THIRD_WIDTH / 3, MM.MUD_REPORT_HEADER_HEIGHT, 'center', mudReport);

    const leftData = [
        [moment.unix(test.testDate).tz(well.timeZoneName).format("MMM DD YYYY, h:mm a")],
        [well.spudDate === '' ? '' : moment.unix(Date.parse(well.spudDate) / 1000).tz('utc').format('MM/DD/YYYY')],
        [test.presentActivity],
    ];
    let tempLeft = MM.PAGE_BORDER_WIDTH + MM.ONE_THIRD_WIDTH / 3;
    let tempWidth = MM.ONE_THIRD_WIDTH * 2 / 3;
    textArrayInBoxes(leftData, tempLeft, MM.PAGE_BORDER_WIDTH, tempWidth, MM.MUD_REPORT_HEADER_HEIGHT, 'center', mudReport);

    const imageWidth = 40;
    const imageHeight = 23;

    mudReport.addImage(
        logoBlob,
        'PNG',
        MM.PAGE_BORDER_WIDTH + MM.ONE_THIRD_WIDTH + ( MM.ONE_THIRD_WIDTH - imageWidth ) / 2,
        MM.PAGE_BORDER_WIDTH + ( MM.MUD_REPORT_HEADER_HEIGHT - imageHeight) / 2,
        imageWidth,
        imageHeight,
    );



    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH;
    mudReport.setFontSize(14);
    textCenteredInBox(`${test.mudProperty} Report`, tempLeft, MM.PAGE_BORDER_WIDTH, MM.ONE_THIRD_WIDTH, MM.MUD_REPORT_HEADER_HEIGHT / 3, mudReport);

    mudReport.setFontSize(7);
    mudReport.setFontStyle('bold');

    tempTextArr = [
        ['DEPTH (MD)', 'DEPTH (TVD)', 'BIT DEPTH']
    ];

    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH;
    let tempTop = MM.PAGE_BORDER_WIDTH + MM.MUD_REPORT_HEADER_HEIGHT / 3;
    const tempHeight = MM.MUD_REPORT_HEADER_HEIGHT / 6;
    tempWidth = MM.ONE_THIRD_WIDTH;
    textArrayInBoxes(tempTextArr, tempLeft, tempTop, tempWidth, tempHeight, 'center', mudReport);

    mudReport.setFontStyle('normal');
    tempTextArr = [
        [dr.RoundString(test.depthMD), dr.RoundString(test.depthTVD), dr.RoundString(test.depthBit)],
    ];

    tempTop += MM.MUD_REPORT_HEADER_HEIGHT / 6;
    textArrayInBoxes(tempTextArr, tempLeft, tempTop, tempWidth, tempHeight, 'center', mudReport);

    mudReport.setFontStyle('bold');
    tempTextArr = ['K', 'O', 'P'];
    tempTop = MM.MUD_REPORT_HEADER_HEIGHT * 2 / 3 + MM.PAGE_BORDER_WIDTH + 0.5;
    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH + MM.ONE_THIRD_WIDTH / 24;
    mudReport.text(
        tempTextArr,
        tempLeft,
        tempTop,
        {
            align: 'center',
            baseline: 'top',
            lineHeightFactor: 1,
        }
    );

    tempTextArr = ['E', 'O', 'C'];
    tempLeft += MM.ONE_THIRD_WIDTH / 2;
    mudReport.text(
        tempTextArr,
        tempLeft,
        tempTop,
        {
            align: 'center',
            baseline: 'top',
            lineHeightFactor: 1,
        }
    );

    const headerArr: any = [
        ['MD', undefined],
        ['TVD', undefined],
    ];

    const dataKOP: any = [
        [undefined, test.kopMD],
        [undefined, test.kopTVD],
    ];

    const dataEOP: any = [
        [undefined, test.eocMD],
        [undefined, test.eocTVD],
    ];

    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH + MM.ONE_THIRD_WIDTH / 12;
    tempTop = MM.MUD_REPORT_HEADER_HEIGHT * 2 / 3 + MM.PAGE_BORDER_WIDTH;

    textArrayInBoxes(headerArr, tempLeft, tempTop, MM.ONE_THIRD_WIDTH * 5 / 12, MM.MUD_REPORT_HEADER_HEIGHT / 3, 'center', mudReport);

    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataKOP, tempLeft, tempTop, MM.ONE_THIRD_WIDTH * 5 / 12, MM.MUD_REPORT_HEADER_HEIGHT / 3, 'center', mudReport);

    tempLeft += MM.ONE_THIRD_WIDTH / 2;
    textArrayInBoxes(dataEOP, tempLeft, tempTop, MM.ONE_THIRD_WIDTH * 5 / 12, MM.MUD_REPORT_HEADER_HEIGHT / 3, 'center', mudReport);

    mudReport.setFontStyle('bold');
    textArrayInBoxes(headerArr, tempLeft, tempTop, MM.ONE_THIRD_WIDTH * 5 / 12, MM.MUD_REPORT_HEADER_HEIGHT / 3, 'center', mudReport);
    

};

export const populateWellInfo = (test: ITest, well: IWell, mudReport: jsPdf) => {
    let tempTextArr: any = [
        ['OPERATOR', 'CONTRACTOR', 'MUD TYPE'],
        [undefined],
        ['WELL NAME & NUMBER', 'RIG NUMBER', well.state === 'TX' ? 'SECTION / TOWNSHIP / BLOCK' : 'SECTION / TOWNSHIP / RANGE'],
        [undefined],
        ['OPERATORS', 'CONTRACTORS', 'FIELD OR BLOCK'],
        [undefined],
    ];

    let tempLeft = MM.PAGE_BORDER_WIDTH;
    let tempTop = MM.WELL_INFO_TOP;
    let tempHeight = MM.WELL_INFO_HEIGHT;
    let tempWidth = MM.FULL_WIDTH;
    mudReport.setFontStyle('bold');
    textArrayInBoxes(tempTextArr, tempLeft, tempTop, tempWidth, tempHeight, 'left', mudReport);

    const section = well.state === 'TX' 
    ? well.shlSection + ' / ' + well.shlTownship + well.shlTownshipDirection + ' / ' + well.shlBlock + well.shlBlockDirection
    : well.shlSection + ' / ' + well.shlTownship + well.shlTownshipDirection + ' / ' + well.shlRange + well.shlRangeDirection;

    tempTextArr = [
        [well.operator.operatorName, well.contractor.contractorName, test.mudProperties.fluidType],
        [undefined],
        [well.wellName, well.rigNumber, undefined],
        [undefined],
        [contactRepresentatives(test.operatorContacts), contactRepresentatives(test.contractorContacts), undefined],
        [undefined]
    ];
    tempTop += MM.WELL_INFO_HEIGHT / 6;
    mudReport.setFontStyle('normal');
    textArrayInBoxes(tempTextArr, tempLeft, tempTop, tempWidth, tempHeight, 'center', mudReport);

    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH;
    tempTop = MM.WELL_INFO_TOP + MM.WELL_INFO_HEIGHT * 5 / 6;
    tempHeight = MM.WELL_INFO_HEIGHT / 6;
    tempWidth = MM.ONE_THIRD_WIDTH * 2 / 3;
    textCenteredInBox(well.fieldAreaUnit, tempLeft, tempTop, tempWidth, tempHeight, mudReport);

    tempLeft += tempWidth;
    tempWidth = MM.ONE_THIRD_WIDTH / 3;
    textCenteredInBox(well.state, tempLeft, tempTop, tempWidth, tempHeight, mudReport);
    
    mudReport.setFontStyle('bold');
    tempTop -= tempHeight;
    textLeftJustifiedInBox('ST / PROV', tempLeft, tempTop, tempWidth, tempHeight, mudReport);

    // SECTION / TOWNSHIP / BLOCK   |   COUNTY / PARISH
    mudReport.setFontStyle('normal');

    tempLeft = MM.PAGE_BORDER_WIDTH + MM.TWO_THIRD_WIDTH;
    tempTop = MM.WELL_INFO_TOP + MM.WELL_INFO_HEIGHT * 3 / 6;
    tempHeight = MM.WELL_INFO_HEIGHT / 6;
    tempWidth = MM.ONE_THIRD_WIDTH * 2 / 3;
    textCenteredInBox(section, tempLeft, tempTop, tempWidth, tempHeight, mudReport);

    tempLeft += tempWidth;
    tempWidth = MM.ONE_THIRD_WIDTH / 3;
    textCenteredInBox(well.countyParish, tempLeft, tempTop, tempWidth, tempHeight, mudReport);
    
    mudReport.setFontStyle('bold');
    tempTop -= tempHeight;
    textLeftJustifiedInBox('COUNTY', tempLeft, tempTop, tempWidth, tempHeight, mudReport);
    
};

export const populateDrillString = (test: ITest, mudReport: jsPdf) => {
    const headerArr: any = [
        ['DRILLING ASSEMBLY'],
        ['Type', 'Length', 'Type', 'Length']
    ];

    fillEmptyRows(headerArr, MM.DRILL_STRING_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(headerArr, MM.PAGE_BORDER_WIDTH, MM.DRILL_STRING_TOP, MM.DRILL_STRING_WIDTH, MM.DRILL_STRING_HEIGHT, 'center', mudReport);

    const reportDrillString = [
        {
            length: 0,
            type: 'Misc',
        },
        {
            length: 0,
            type: 'Tools',
        },
        {
            length: 0,
            type: 'DC1',
        },
        {
            length: 0,
            type: 'DC2',
        },
        {
            length: 0,
            type: 'DC3',
        },
        {
            length: 0,
            type: 'HWDP1',
        },
        {
            length: 0,
            type: 'HWDP2',
        },
        {
            length: 0,
            type: 'DP1',
        },
        {
            length: 0,
            type: 'DP2',
        },
        {
            length: 0,
            type: 'DP3',
        },
    ];

    test.drillStringAssembly.forEach(dsai => {
        const reportItem = reportDrillString.find(rdsi => rdsi.type === dsai.itemTypeShortName);
        if (reportItem) {
            reportItem.length += Number(dsai.length);
        }
    });

    // So above we created the order that we want the items in per MUD-2502 then we populate
    // the lengths.

    // Now below we are going to put the data into an array that we will start at the top of the section
    // So there are two blank lines.  Then 5 lines of data.  Two columns formatted per MUD-2502 - MUD-2504
    const drillStringData = [undefined, undefined] as unknown as any[][];
    reportDrillString.filter(rdsi => rdsi.length !== 0).forEach((rdsi, index) => {
        if(index < 5) {
            drillStringData.push([rdsi.type, rdsi.length, undefined, undefined]);
        } else {
            // We want the index 5 item to go into the first non-blank row of the second column
            drillStringData[index - 3][2] = rdsi.type;
            drillStringData[index - 3][3] = rdsi.length;
        }
    });
    fillEmptyRows(drillStringData, MM.DRILL_STRING_ROWS);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(drillStringData, MM.PAGE_BORDER_WIDTH, MM.DRILL_STRING_TOP, MM.DRILL_STRING_WIDTH, MM.DRILL_STRING_HEIGHT, 'center', mudReport);

};

export const populateCasingData = (test: ITest, mudReport: jsPdf) => {
    const headerArr = [
        ['CASING DATA'],
        ['OD', 'ID', 'Top', 'Bottom'],
    ];
    fillEmptyRows(headerArr, MM.CASING_DATA_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(headerArr, MM.CASING_DATA_LEFT, MM.CASING_DATA_TOP, MM.CASING_DATA_WIDTH, MM.CASING_DATA_HEIGHT, 'center', mudReport);

    const dataArr: any = [
        [undefined],
        [undefined],
    ];

    test.intervals.slice().reverse().forEach(element => {
        if (Array.isArray(element.casings)) {
            element.casings.slice().reverse().forEach(casing => {
                dataArr.push([
                    dr.RoundStrict(casing.casingOD, 3),
                    dr.RoundStrict(casing.casingID, 3),
                    dr.RoundString(casing.casingTop),
                    dr.RoundString(casing.casingBottom),
                ]);
            });
        }
    });

    fillEmptyRows(dataArr, MM.CASING_DATA_ROWS);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataArr, MM.CASING_DATA_LEFT, MM.CASING_DATA_TOP, MM.CASING_DATA_WIDTH, MM.CASING_DATA_HEIGHT, 'center', mudReport);

};

export const populateBitData = (test: ITest, mudReport: jsPdf) => {
    const bigHeaders = [
        ['BIT DATA', ...Array(6)],
        ['JET SIZE', ...Array(6)],
    ];
    mudReport.setFontStyle('bold');
    textArrayInBoxes(bigHeaders, MM.PAGE_BORDER_WIDTH, MM.BIT_DATA_TOP, MM.BIT_DATA_WIDTH, MM.BIT_DATA_HEIGHT, 'center', mudReport);

    const headers = [
        [undefined, 'NO', 'SIZE', 'TYPE', 'DEPTH IN', 'ROP', undefined],
        [...Array(5), 'WOB', undefined],
        [...Array(4), 'TFA', 'RPM', undefined],
        [...Array(5), 'TORQUE', undefined]
    ];
    textArrayInBoxes(headers, MM.PAGE_BORDER_WIDTH, MM.BIT_DATA_TOP, MM.BIT_DATA_WIDTH, MM.BIT_DATA_HEIGHT, 'center', mudReport);
    const bitData = test.bitData.bits && test.bitData.bits[0] ? test.bitData.bits[0] : {} as IDrillBit;
    const dataArr = [
        [...Array(6), dr.RoundString(test.bitData.rop)],
        [
            undefined,
            dr.RoundString(bitData.bitNumber),
            bitData.bitSize ? dr.RoundStrict(Number(bitData.bitSize), 3) : '',
            bitData.bitType,
            dr.RoundString(Number(bitData.depthIn)),
            undefined,
            dr.RoundString(test.bitData.wob)
        ],
        [...Array(6), dr.RoundString(test.bitData.rpm)],
        [...Array(4), dr.RoundStrict(bitData.tfa, 2), undefined, dr.RoundString(test.bitData.torqueMax)]
    ];

    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataArr, MM.PAGE_BORDER_WIDTH, MM.BIT_DATA_TOP, MM.BIT_DATA_WIDTH, MM.BIT_DATA_HEIGHT, 'center', mudReport);
    
    const bitJetArrayRow1 = bitData.jetSizes ? bitData.jetSizes.reduce((total, num, index) => {
        if (index < 6 ) {
            total.push(dr.RoundString(Number(num)));
        }
        return total;
    }, [] as any) : [];

    while( bitJetArrayRow1.length < 6) {
        bitJetArrayRow1.push(undefined);
    }
    
    const bitJetArrayRow2 = bitData.jetSizes ? bitData.jetSizes.reduce((total, num, index) => {
        if (index >= 6 && index < 12 ) {
            total.push(dr.RoundString(Number(num)));
        }
        return total;
    }, [] as any) : [];
    
    while( bitJetArrayRow2.length < 6) {
        bitJetArrayRow2.push(undefined);
    }

    const bitJetArr = [
        [undefined],
        [undefined],
        [...Array(2), ...bitJetArrayRow1, ...Array(6)],
        [...Array(2), ...bitJetArrayRow2, ...Array(6)],
    ];
    textArrayInBoxes(bitJetArr, MM.PAGE_BORDER_WIDTH, MM.BIT_DATA_TOP, MM.BIT_DATA_WIDTH, MM.BIT_DATA_HEIGHT, 'center', mudReport);
    
};

export const populateCirVolData = (test: ITest, mudReport: jsPdf) => {
    const headersArr: any = [
        ['CIRCULATION VOLUME'],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        ['OTHER VOLUME']
    ];
    fillEmptyRows(headersArr, MM.CIRC_VOL_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(headersArr, MM.CIRC_VOL_LEFT, MM.CIRC_VOL_TOP, MM.CIRC_VOL_WIDTH, MM.CIRC_VOL_HEIGHT, 'center', mudReport);

    const headerName: any = [
        [undefined],
        ['Pit Volume +'],
        ['String Cap +'],
        ['Annular Vol +'],
        ['Circ Vol ='],
        [undefined],
        ['Vol Below Bit'],
        ['String Disp'],
    ];

    textArrayInBoxes(headerName,
        MM.CIRC_VOL_LEFT, 
        MM.CIRC_VOL_TOP, 
        MM.CIRC_VOL_WIDTH * 2 / 3,
        MM.CIRC_VOL_HEIGHT,
        'left',
        mudReport
    );

    const stringDisp = test.drillStringAssembly.reduce((total:number, num) => {
        return total + num.disp * num.length;
    }, 0);
    const dataArr: any = [
        [undefined],
        [dr.RoundString(test.pumpData.pitVolume)],
        [dr.RoundString(test.pumpData.surfaceToBitBarrels)],
        [dr.RoundString(test.pumpData.bottomsUpBarrels)],
        [dr.RoundString(test.pumpData.activeVolumeBarrels)],
        [undefined],
        [dr.RoundString(test.pumpData.volumeBelowBit)],
        [dr.RoundString(stringDisp)],
    ];

    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataArr,
        MM.CIRC_VOL_LEFT + MM.CIRC_VOL_WIDTH * 3 / 5,
        MM.CASING_DATA_TOP,
        MM.CIRC_VOL_WIDTH * 2 / 5,
        MM.CIRC_VOL_HEIGHT,
        'center',
        mudReport
    );
};

export const populatePumpData = (test: ITest, mudReport: jsPdf) => {
    const headerName: any = [
        ['PUMP']
    ];
    fillEmptyRows(headerName, MM.PUMP_DATA_ROWS);

    const namesArr: any = [
        [undefined],
        ['Pump % Eff'],
        ['Liner Size'],
        ['Stroke Length'],
        ['bbl/stk'],
        ['stk/min'],
        ['gal/min'],
        ['bbl/min'],
    ];
    fillEmptyRows(namesArr, MM.PUMP_DATA_ROWS);

    mudReport.setFontStyle('bold');
    textArrayInBoxes(headerName, MM.PUMP_DATA_LEFT, MM.PUMP_DATA_TOP, MM.PUMP_DATA_WIDTH / 3, MM.PUMP_DATA_HEIGHT, 'center', mudReport);
    textArrayInBoxes(namesArr, MM.PUMP_DATA_LEFT, MM.PUMP_DATA_TOP, MM.PUMP_DATA_WIDTH / 3, MM.PUMP_DATA_HEIGHT, 'left', mudReport);

    const dataHeader: any = [
        ['#1', '#2', '#3', '#4'],
    ];
    fillEmptyRows(dataHeader, MM.PUMP_DATA_ROWS);
    const dataArr: any = [
        [undefined],
    ];
    fillEmptyRows(dataArr, MM.PUMP_DATA_ROWS);
    test.pumpData.pumps.forEach((element, index) => {
        if (index < 4) {
            dataArr[1][index] = dr.RoundStrict(element.efficiency);
            dataArr[2][index] = dr.RoundMax(element.linerSize, 2);
            dataArr[3][index] = dr.RoundMax(element.stkLength, 2);
            dataArr[4][index] = dr.RoundStrict(element.bblStk, 4);
            dataArr[5][index] = dr.RoundStrict(element.stkMin);
            dataArr[6][index] = dr.RoundStrict(element.galMin);
            dataArr[7][index] = dr.RoundStrict(element.bblMin, 2);
        }
    });
    
    dataArr.forEach((element: any) => {
        while(element.length < 4) {
            element.push(undefined);
        }    
    });
    textArrayInBoxes(dataHeader,
        MM.PUMP_DATA_LEFT + MM.PUMP_DATA_WIDTH / 3,
        MM.PUMP_DATA_TOP,
        MM.PUMP_DATA_WIDTH * 2 / 3,
        MM.PUMP_DATA_HEIGHT,
        'center',
        mudReport,  
    );
    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataArr,
        MM.PUMP_DATA_LEFT + MM.PUMP_DATA_WIDTH / 3,
        MM.PUMP_DATA_TOP,
        MM.PUMP_DATA_WIDTH * 2 / 3,
        MM.PUMP_DATA_HEIGHT,
        'center',
        mudReport,
    );

};

export const populateCircData = (test: ITest, mudReport: jsPdf) => {

    const headerArr1 = [
        ['Annular Vel', ...Array(3)],
        ['DP', ...Array(7)],
        ['DC', ...Array(7)],
    ];

    mudReport.setFontStyle('bold');
    textArrayInBoxes(headerArr1, MM.CIRC_DATA_LEFT, MM.CIRC_DATA_TOP, MM.CIRC_DATA_WIDTH, MM.CIRC_DATA_HEIGHT, 'center', mudReport);

    const headerArr2 = [
        ['CIRC DATA', 'String Cap', 'Annular Vol', 'Total Hole', 'Circ Vol'],
        ['Strokes', ...Array(4)],
        ['Time', ...Array(4)],
    ];

    const tempLeft = MM.CIRC_DATA_LEFT + MM.CIRC_DATA_WIDTH / 4;
    const tempWidth = MM.CIRC_DATA_WIDTH * 3 / 4;
    textArrayInBoxes(headerArr2, tempLeft, MM.CIRC_DATA_TOP, tempWidth, MM.CIRC_DATA_HEIGHT, 'center', mudReport);

    const dataArr1 = [
        [undefined],
        [undefined, dr.RoundString(test.pumpData.avdp), ...Array(6)],
        [undefined, dr.RoundString(test.pumpData.avdc), ...Array(6)],
    ];

    const dataArr2: any = [
        [undefined],
        [
            undefined,
            dr.RoundString(test.pumpData.surfaceToBitStks),
            dr.RoundString(test.pumpData.bottomsUpStks),
            dr.RoundString(test.pumpData.surfaceToSurfaceStks),
            dr.RoundString(test.pumpData.activeVolumeStks),
        ],
        [
            undefined,
            dr.RoundString(test.pumpData.surfaceToBitMinutes),
            dr.RoundString(test.pumpData.bottomsUpTime),
            dr.RoundString(test.pumpData.surfaceToSurfaceMinutes),
            dr.RoundString(test.pumpData.activeVolumeMinutes),
        ],
    ];
    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataArr1, MM.CIRC_DATA_LEFT, MM.CIRC_DATA_TOP, MM.CIRC_DATA_WIDTH, MM.CIRC_DATA_HEIGHT, 'center', mudReport);
    textArrayInBoxes(dataArr2, tempLeft, MM.CIRC_DATA_TOP, tempWidth, MM.CIRC_DATA_HEIGHT, 'center', mudReport);
    
};

export const populateMudProperties = (test: ITest, mudReport: jsPdf) => {
    
    
    let centeredHeadersBoth: any = [
        ['MUD PROPERTY SPECIFICATIONS'],
        ['Weight', "Vis", test.mudProperties.mudPropOption1, test.mudProperties.mudPropOption2, test.mudProperties.mudPropOption3],
        [undefined],
        ['Mud Properties'],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        ['Meter Readings'],
        ['600', '300', '200', '100', '6', '3'],
        [undefined],
        [undefined],
        [undefined],
        ['Gel Strength (lb/100 ft\xB2)'],
        ['10 Sec', '10 Min', '30 min'],
        
    ];

    let leftHeadersBoth: any = [
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        ['Sample Time'],
        ['Sample Location'],
        ['Flowline Temp Deg\xBA'],
        ['Depth (ft)'],
        ['Mud Weight (ppg)'],
        ['Funnel Vis (sec/qt)'],
        [undefined],
        [undefined],
        [undefined],
        ['Plastic Vis (cp)'],
        ['Yield Point (lb/100 ft\xB2)'],
        [undefined],
        [undefined],
        [undefined],
        ['API Filtrate'],
        ['HTHP Filtrate'],
        ['Cake Thickness (API/HTHP)'],
        ['Retort Solids %'],
        ['Retort Oil %'],
        ['Retort Water %'],
        [test.propertyType === 'Brine Emulsion' ? 'Salt % Adj For Water Vol' : 'Solids Adj Salt (% by Vol)'],
    ];

    fillEmptyRows(centeredHeadersBoth, 25);

    let saltVolumeAdjPcnt;

    if (test.mudProperty === 'Oil Based Mud') {
        saltVolumeAdjPcnt = test.mudProperties.saltVolumePcnt;
    } else if (test.propertyType === 'Brine Emulsion') {
        saltVolumeAdjPcnt = test.mudProperties.saltVolumeAdjPcnt;
    } else {
        saltVolumeAdjPcnt = test.mudProperties.lowGravityPcnt;
    }

    let dataBoth: any = [
        [undefined],
        [undefined],
        [
            test.mudProperties.mudWeightPpg,
            test.mudProperties.viscositySecQt,
            test.mudProperties.mudPropValue1,
            test.mudProperties.mudPropValue2,
            test.mudProperties.mudPropValue3,
        ],
        [undefined],
        [...Array(3), moment.unix(test.mudProperties.sampleSourceTime).format("h:mm a")],
        [...Array(3), test.mudProperties.sampleSource],
        [...Array(3), dr.RoundString(test.mudProperties.flowLineTemp, 2)],
        [...Array(3), dr.RoundString(test.depthMD)],
        [...Array(3), dr.RoundStrict(test.mudProperties.mudWeight, 2)],
        [
            ...Array(2),
            dr.RoundString(test.mudProperties.funnelViscosityDefF) + ' °F',
            dr.RoundString(test.mudProperties.funnelViscosity),
        ],
        [undefined],
        [undefined],
        [
            dr.RoundString(test.mudProperties.rpmDegF600),
            dr.RoundString(test.mudProperties.rpmDegF300),
            dr.RoundString(test.mudProperties.rpmDegF200),
            dr.RoundString(test.mudProperties.rpmDegF100),
            dr.RoundString(test.mudProperties.rpmDegF6),
            dr.RoundString(test.mudProperties.rpmDegF3),
        ],
        [
            ...Array(2),
            dr.RoundString(test.mudProperties.rpmDegF, 1) + ' °F',
            dr.RoundString(test.mudProperties.plasticViscosity, 4),
        ],
        [...Array(3), dr.RoundString(test.mudProperties.yeildPoint, 4)],
        [undefined],
        [undefined],
        [
            dr.RoundString(test.mudProperties.gelStrength10sec, 4),
            dr.RoundString(test.mudProperties.gelStrength10min, 4),
            dr.RoundString(test.mudProperties.gelStrength30min, 4),
        ],
        [
            ...Array(2),
            `${test.mudProperties.mudTempApi} °F`,
            test.mudProperties.filtrateApi,
        ],
        [
            ...Array(2),
            `${test.mudProperties.mudTempHthp} °F`,
            test.mudProperties.filtrateHthp,
        ],
        [...Array(3), `${test.mudProperties.cakeThicknessApi} / ${test.mudProperties.cakeThicknessHthp}`],
        [...Array(3), dr.RoundStrict(test.mudProperties.retortContentSolids, 2)],
        [...Array(3), dr.RoundStrict(test.mudProperties.retortContentOil, 2)],
        [...Array(3), dr.RoundStrict(test.mudProperties.retortContentWater, 2)],
        [...Array(3), dr.RoundStrict(saltVolumeAdjPcnt, 2)],
    ];

    const highGravPcnt = test.mudProperties.highGravityPcnt < 0.1 && test.mudProperties.highGravityPcnt > -0.1 ? 0 : test.mudProperties.highGravityPcnt;
    const highGravityLbBbl = highGravPcnt === 0 ? 0 : test.mudProperties.highGravityLbBbl;
    const displayAsg = 
        test.weighted !== false
        || test.mudProperties.showNormalASG
        ? test.mudProperties.asg : test.mudProperties.unweightedASG;
        
    const dataBothBottom = [
        [...Array(3), dr.RoundStrict(displayAsg, 4)],
        [undefined],
        [undefined],
        [
            undefined,
            dr.RoundStrict(highGravPcnt, 2),
            test.propertyType === 'Brine Emulsion' ? dr.RoundStrict(test.mudProperties.drillSolidsPcnt, 2) : dr.RoundStrict(test.mudProperties.lowGravityPcnt, 2),
            test.mudProperty === 'Oil Based Mud' ? undefined : dr.RoundStrict(test.mudProperties.drillSolidsPcnt, 2),
        ],
        [
            undefined,
            dr.RoundStrict(highGravityLbBbl, 2),
            dr.RoundStrict(test.mudProperties.lowGravityLbBbl, 2),
            test.mudProperty === 'Oil Based Mud' ? undefined :  dr.RoundStrict(test.mudProperties.drillSolidsLbBbl, 2),
        ],
        [...Array(3), `${test.mudProperties.lcm}`],
        [...Array(3), `${test.mudProperties.optionalField1Value}`],
        [...Array(3), `${test.mudProperties.optionalField2Value}`],
    ];
    
    if(test.mudProperty === 'Oil Based Mud') {
        const centeredHeaderOil = [
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            ['SOLIDS'],
            ['UNITS', 'HGS', 'LGS', undefined],
            ['%', undefined, undefined, undefined],
            ['lb/bbl', undefined, undefined, undefined],

        ];
        centeredHeadersBoth = centeredHeadersBoth.concat(centeredHeaderOil);
        fillEmptyRows(centeredHeadersBoth, MM.MUD_PROP_SPEC_ROWS_OBM);

        const leftHeaderOil = [
            ['Salt Content Water Phase'],
            ['Oil / Water Ratio'],
            ['Mud Alkalinity (Pom)'],
            ['Chloride (Whole Mud)'],
            ['Chloride Ion (mg/L)'],
            ['CaCl2 (mg/L) WPS'],
            ['Lime Content (lb/bbl)'],
            ['Versenate'],
            ['Electric Stablity'],
            ['ASG'],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            ['LCM'],
            [test.mudProperties.optionalField1Name],
            [test.mudProperties.optionalField2Name],
        ];
        leftHeadersBoth = leftHeadersBoth.concat(leftHeaderOil);
        fillEmptyRows(leftHeadersBoth, MM.MUD_PROP_SPEC_ROWS_OBM);

        const dataOil = [
            [...Array(3), dr.RoundString(test.mudProperties.saltContentWaterPhase, 4)],
            [...Array(3), `${dr.RoundStrict(test.mudProperties.oilWaterRatioFrom)} / ${(100 - Number(test.mudProperties.oilWaterRatioFrom.toFixed(0))).toFixed(0)}`],
            [...Array(3), dr.RoundString(test.mudProperties.mudAlkalinityPom, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.chlorideClsn, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.chlorideMgL, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.cacl2wps, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.limeContent, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.versenate, 4)],
            [...Array(3), dr.RoundString(test.mudProperties.electricalStability, 4)],
        ];
        dataBoth = dataBoth.concat(dataOil).concat(dataBothBottom);
        fillEmptyRows(dataBoth, MM.MUD_PROP_SPEC_ROWS_OBM);

    } else {
        
        const centeredHeaderWater = [
            [undefined],
            [undefined],
            [undefined],
            ['Alkalinity'],
            ['PM', 'PF', 'MF'],
            [undefined],
            [undefined],
            [undefined],
            ['Hardness (mg/L)'],
            ['Total', 'Calcium', 'Magnesium'],
            [undefined],
            [undefined],
            [undefined],
            ['SOLIDS'],
            ['UNITS', 'HGS', 'LGS', 'DS'],
            ['%', undefined, undefined, undefined],
            ['lb/bbl', undefined, undefined, undefined],
        ];
        centeredHeadersBoth = centeredHeadersBoth.concat(centeredHeaderWater);
        fillEmptyRows(centeredHeadersBoth, MM.MUD_PROP_SPEC_ROWS_WBM);

        const leftHeaderWater = [
            ['Sand Content'],
            ['MBT (lb/bbl / % by Vol)'],
            ['pH'],
            [undefined],
            [undefined],
            [undefined],
            ['Chlorides (mg/L)'],
            [test.propertyType === 'Brine Emulsion' ? 'Salt % Vol' : 'Calc Salt (% by Vol)'],
            [undefined],
            [undefined],
            [undefined],
            ['Excess Lime (lb/bbl)'],
            ['ASG'],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            ['LCM'],
            [test.mudProperties.optionalField1Name],
            [test.mudProperties.optionalField2Name],
        ];

        leftHeadersBoth = leftHeadersBoth.concat(leftHeaderWater);
        fillEmptyRows(leftHeadersBoth, MM.MUD_PROP_SPEC_ROWS_WBM);

        const dataWater = [
            [...Array(3), dr.RoundString(test.mudProperties.sandContent, 4)],
            [...Array(3), `${dr.RoundString(test.mudProperties.methyleneBlueTest, 4)} / ${dr.RoundStrict(test.mudProperties.bentonitePcnt)}`],
            [...Array(3), dr.RoundString(test.mudProperties.pHReading, 4)],
            [undefined],
            [undefined],
            [
                dr.RoundString(test.mudProperties.mudAlkalinityPm, 4),
                dr.RoundString(test.mudProperties.filtrateAlkalinityPf, 4),
                dr.RoundString(test.mudProperties.filtrateAlkalinityMf, 4),
            ],
            [...Array(3), dr.RoundString(test.mudProperties.chlorideMgL, 4)],
            [...Array(3), dr.RoundStrict(test.mudProperties.saltVolumePcnt, 2)],
            [undefined],
            [undefined],
            [
                dr.RoundStrict(test.mudProperties.totalHardness, 2),
                dr.RoundStrict(test.mudProperties.calcium, 2),
                dr.RoundStrict(test.mudProperties.magnesium, 2),
            ],
            [...Array(3), dr.RoundStrict(test.mudProperties.excessLime, 2)],
        ];

        dataBoth = dataBoth.concat(dataWater).concat(dataBothBottom);
        fillEmptyRows(dataBoth, MM.MUD_PROP_SPEC_ROWS_WBM);

    }

    mudReport.setFontStyle('bold');
    textArrayInBoxes(centeredHeadersBoth, MM.PAGE_BORDER_WIDTH, MM.MUD_PROP_SPEC_TOP, MM.MUD_PROP_SPEC_WIDTH, MM.MUD_PROP_SPEC_HEIGHT, 'center', mudReport);
    textArrayInBoxes(leftHeadersBoth, MM.PAGE_BORDER_WIDTH, MM.MUD_PROP_SPEC_TOP, MM.MUD_PROP_SPEC_WIDTH, MM.MUD_PROP_SPEC_HEIGHT, 'left', mudReport);

    mudReport.setFontStyle('normal');
    textArrayInBoxes(dataBoth, MM.PAGE_BORDER_WIDTH, MM.MUD_PROP_SPEC_TOP, MM.MUD_PROP_SPEC_WIDTH, MM.MUD_PROP_SPEC_HEIGHT, 'center', mudReport);

};

export const populateTourly = (test: ITest, mudReport: jsPdf) => {
    mudReport.setFontStyle('bold');
    textCenteredInBox('TOURLY TREATMENT RECOMMENDATIONS', MM.TOURLY_LEFT, MM.TOURLY_TOP, MM.TOURLY_WIDTH, MM.SECTION_HEADER_HEIGHT, mudReport);

    mudReport.setFontStyle('normal');
    const tourlyArr = mudReport.splitTextToSize(test.remarks.tourlyTreatment, MM.TOURLY_WIDTH - 8);
    while(mudReport.getTextDimensions(tourlyArr).h > MM.TOURLY_HEIGHT - MM.SECTION_HEADER_HEIGHT - 1) {
        tourlyArr.splice(tourlyArr.length - 1);
    }
    mudReport.text(
        tourlyArr,
        MM.TOURLY_LEFT + 1,
        MM.TOURLY_TOP + MM.SECTION_HEADER_HEIGHT + 1,
        {
            align: 'left',
            baseline: 'top',
        }
    );
};


export const populateRecap = (test: ITest, mudReport: jsPdf) => {
    mudReport.setFontStyle('bold');
    textCenteredInBox('RECAP REMARKS', MM.RECAP_LEFT, MM.RECAP_TOP, MM.RECAP_WIDTH, MM.SECTION_HEADER_HEIGHT, mudReport);

    mudReport.setFontStyle('normal');
    const recapArr = mudReport.splitTextToSize(test.remarks.mudReportRecap, MM.RECAP_WIDTH - 2);
    while(mudReport.getTextDimensions(recapArr).h > MM.RECAP_HEIGHT - MM.SECTION_HEADER_HEIGHT - 1) {
        recapArr.splice(recapArr.length - 1);
    }
    
    const height = mudReport.getTextDimensions(recapArr).h;
    mudReport.text(
        recapArr,
        MM.RECAP_LEFT +MM.RECAP_WIDTH / 2,
        MM.RECAP_TOP + MM.SECTION_HEADER_HEIGHT + (MM.RECAP_HEIGHT - MM.SECTION_HEADER_HEIGHT - height) / 2,
        {
            align: 'center',
            baseline: 'middle',
        }
    );
};

export const populateSurveys = (test: ITest, mudReport: jsPdf) => {
    const surveyHeaders: any = [
        ['SURVEYS'],
        ['Depth', 'Azimuth', 'Inclination'],
    ];
    fillEmptyRows(surveyHeaders, MM.SURVEY_ROWS);

    const surveyData: any = [
        [undefined],
        [undefined],
    ];
    const surveys = test.surveys.slice().sort((a, b) => b.surveyDateTime - a.surveyDateTime);
    for(let i = 0; i < MM.SURVEY_ROWS - 2; ++i) {
        if(surveys[i]) {
            surveyData.push([
                surveys[i].depthMD,
                surveys[i].azimuth,
                dr.RoundMax(surveys[i].inclination, 2),
            ]);
        }
    }
    fillEmptyRows(surveyData, MM.SURVEY_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(surveyHeaders, MM.SURVEY_LEFT, MM.SURVEY_TOP, MM.SURVEY_WIDTH, MM.SURVEY_HEIGHT, 'center', mudReport);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(surveyData, MM.SURVEY_LEFT, MM.SURVEY_TOP, MM.SURVEY_WIDTH, MM.SURVEY_HEIGHT, 'center', mudReport);

};

export const populateExternalStorage = (test: ITest, mudReport: jsPdf) => {
    const externalHeaders:any = [
        ['EXTERNAL STORAGE'],
        ['Type of Mud', undefined ],
    ];
    const externalHeadersRight = [
        [undefined],
        [...Array(2), 'Tanks', 'Tot Vol'],
    ];
    const externalHeadersLeft: any = [
        [undefined],
        [undefined],
    ];

    const externalData:any = [
        [undefined],
        [undefined],
    ];
    const externalList = getExternalStorageList(test);

    externalList.forEach((element:any) => {
        externalData.push([...Array(2), element.count, element.volume]);
        externalHeadersLeft.push([element.mudType, undefined]);
    });
    
    fillEmptyRows(externalHeaders, MM.EXTERNAL_ROWS);
    fillEmptyRows(externalHeadersRight, MM.EXTERNAL_ROWS);
    fillEmptyRows(externalHeadersLeft, MM.EXTERNAL_ROWS);
    fillEmptyRows(externalData, MM.EXTERNAL_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(externalHeaders, MM.EXTERNAL_LEFT, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', mudReport);
    textArrayInBoxes(externalHeadersRight, MM.EXTERNAL_LEFT, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', mudReport);
    textArrayInBoxes(externalHeadersLeft, MM.EXTERNAL_LEFT, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', mudReport);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(externalData, MM.EXTERNAL_LEFT, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', mudReport);

    

};

interface IExternalStorageRow{
    count: number;
    mudType: string;
    volume: number;
}

export const getExternalStorageList = (test: ITest) => {
    const retArr: IExternalStorageRow[] = [];

    for (const externalStorageItem of test.externalStorage) {
        const tempFluidType = externalStorageItem.fluidSubType;
        if (tempFluidType !== '') {

            let foundRow = false;
            for (const externalStorageRow of retArr) {
                if (tempFluidType === externalStorageRow.mudType) {
                    externalStorageRow.count++;
                    externalStorageRow.volume += Number(externalStorageItem.todayFluidAmount);
                    foundRow = true;
                    break;
                }
            }
            if (!foundRow) {
                const tempRow: any = {
                    count: 1,
                    mudType: tempFluidType,
                    volume: Number(externalStorageItem.todayFluidAmount),
                };
                retArr.push(tempRow);
            }
        }
    }
    return retArr.sort((a, b) => {
        return b.volume - a.volume;
    });
};

export const populateVolumeAccounting = (test: ITest, mudReport: jsPdf) => {
    const volActCenteredHeaders: any = [
        ['VOLUME ACCOUNTING'],
        [
            test.mudProperty === 'Oil Based Mud' ? 'OBM' : 'WBM'
        ],
        [
            undefined,
            'Type',
            undefined,
            'Barrels'
        ],

    ];
    const showVolumeAccounting = test.isVolumeAccountingReport;
    fillEmptyRows(volActCenteredHeaders, MM.VOLUME_ROWS);
    const volActLeftHeaders: any = [
        [undefined],
        [undefined],
        [undefined],
        ['Total Daily Additions'],
        ['Vol Transferred In'],
        ['Total Daily Losses'],
        ['Vol Transferred Out'],
        ['Total Vol On Location'],
        ['Base Oil Daily(gal)'],
        ['Base Oil Cumm(gal)'],
        ['Base Oil On Location(gal)'],
    ];

    const transferredIn = test.volumeAccounting.plantVolRecvd + test.volumeAccounting.otherReceived;
    const transferredOut = test.volumeAccounting.plantVolReturned + test.volumeAccounting.otherTransfer;

    const volActData: any = [
        [undefined],
        [undefined],
        [undefined],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(test.volumeAccounting.totalAdditions) : ''],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(transferredIn) : ''],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(test.volumeAccounting.totalLosses) : ''],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(transferredOut) : ''],
        [...Array(3), dr.RoundStrict(test.volumeAccounting.totalVolume)],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(test.volumeAccounting.baseOilUsed) : ''],
        [...Array(3), showVolumeAccounting ? dr.RoundStrict(test.volumeAccounting.cumBaseOilUsed) : ''],
        [...Array(3), dr.RoundStrict(test.volumeAccounting.endingBaseOil)],
    ];

    mudReport.setFontStyle('bold');

    textArrayInBoxes(volActCenteredHeaders, MM.VOLUME_LEFT, MM.VOLUME_TOP, MM.VOLUME_WIDTH, MM.VOLUME_HEIGHT, 'center', mudReport);
    textArrayInBoxes(volActLeftHeaders, MM.VOLUME_LEFT, MM.VOLUME_TOP, MM.VOLUME_WIDTH, MM.VOLUME_HEIGHT, 'left', mudReport);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(volActData, MM.VOLUME_LEFT, MM.VOLUME_TOP, MM.VOLUME_WIDTH, MM.VOLUME_HEIGHT, 'center', mudReport);
};

export const populateHydra = (test: ITest, mudReport: jsPdf) => {
    const hydraCenHeader = [
        ['HYDRAULICS'],
    ];
    fillEmptyRows(hydraCenHeader, MM.HYDRA_ROWS);
    const hydraLeftHeaders: any = [
        [undefined],
        ['Pump Pressure'],
        ['Hydrostatic Pressure'],
        ['ECD'],
        ['Bit Pressure Drop'],
        ['Annular Pressure Drop'],
        ['Hydraulic Horsepower'],
        ['Bit HHP sq in.'],
        ['Impact Force'],
        ['Jet Velocity'],
        ['n value'],
        ['K value'],
    ];
    let hhp = CalcStore.bitHydraulicHp({
        circRate: test.pumpData.totalGalMin,
        jetNozzlePressureDrop: test.bitData.pressureDrop,
    });

    hhp = isNaN(hhp) ? 0 : hhp;
    const drillStringSections = CalcStore.convertDrillStringAssembly(test.drillStringAssembly, test.depthBit);
    const intervalSections = CalcStore.convertIntervals(test.intervals, test.bitData.estHoleSize);
    let pipeSections = CalcStore.calcPipeSections(drillStringSections, intervalSections);
    if(!pipeSections) {
        pipeSections = [];
    }
    let annularPressureDrop = CalcStore.calculateAnnularPressureLoss({
        circRate: test.pumpData.totalGalMin,
        mudWeight: test.mudProperties.mudWeight,
        pipeSections
    });
    if (!isFinite(annularPressureDrop)) {
        annularPressureDrop = 0;
    }
    const hydraData: any = [
        [undefined],
        [...Array(3), dr.RoundStrict(test.pumpData.circPressure)],
        [...Array(3), dr.RoundStrict(test.mudProperties.hydrostaticPsi, 2)],
        [...Array(3), dr.RoundStrict(test.mudProperties.ecd, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.pressureDrop, 2)],
        [...Array(3), dr.RoundStrict(annularPressureDrop, 2)],
        [...Array(3), dr.RoundStrict(hhp, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.hydraulicHorsepower, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.jetImpactForce, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.nozzleVelocity, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.nValue, 2)],
        [...Array(3), dr.RoundStrict(test.bitData.kValue, 2)],
    ];

    mudReport.setFontStyle('bold');
    textArrayInBoxes(hydraCenHeader, MM.HYDRA_LEFT, MM.HYDRA_TOP, MM.HYDRA_WIDTH, MM.HYDRA_HEIGHT, 'center', mudReport);
    textArrayInBoxes(hydraLeftHeaders, MM.HYDRA_LEFT, MM.HYDRA_TOP, MM.HYDRA_WIDTH, MM.HYDRA_HEIGHT, 'left', mudReport);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(hydraData, MM.HYDRA_LEFT, MM.HYDRA_TOP, MM.HYDRA_WIDTH, MM.HYDRA_HEIGHT, 'center', mudReport);
};

export const populateProducts = (test: ITest, mudReport: jsPdf) => {
    const headersOne: any = [
        ['DRILLING FLUID INVENTORY AND COSTS'],
        ['Material', undefined, 'Material', undefined],
    ];
    fillEmptyRows(headersOne, MM.PRODUCT_ROWS);

    const headersTwo: any = [
        [undefined],
        [...Array(2), 'Used', 'Balance', ...Array(2), 'Used', 'Balance'],
    ];
    fillEmptyRows(headersTwo, MM.PRODUCT_ROWS);

    const productDataOne: any = [
        [undefined],
        [undefined],
    ];

    const productDataTwo: any = [
        [undefined],
        [undefined],
    ];

    const headerRows = 2;
    test.productInventorys.slice().sort((a, b,) => {
        return b.totalCost - a.totalCost || a.product.name.localeCompare(b.product.name);
    }).forEach((value, index) => {
        if(index < (MM.PRODUCT_ROWS - headerRows)) {

            productDataOne.push([
                value.product.name,
                undefined,
                undefined,
                undefined,
            ]);
            productDataTwo.push([
                undefined,
                undefined,
                dr.RoundString(value.amountUsed),
                dr.RoundString(value.closingAmount),
                ...Array(4),
            ]);

        } else if (index < (MM.PRODUCT_ROWS - headerRows) * 2) {
            productDataOne[index + headerRows * 2 - MM.PRODUCT_ROWS][2] = value.product.name;
            productDataTwo[index + headerRows * 2 - MM.PRODUCT_ROWS][6] = value.amountUsed;
            productDataTwo[index + headerRows * 2 - MM.PRODUCT_ROWS][7] = value.closingAmount;
        }

    });
    fillEmptyRows(productDataOne, MM.PRODUCT_ROWS);
    fillEmptyRows(productDataTwo, MM.PRODUCT_ROWS);
    mudReport.setFontStyle('bold');
    textArrayInBoxes(headersOne, MM.PRODUCT_LEFT, MM.PRODUCT_TOP, MM.PRODUCT_WIDTH, MM.PRODUCT_HEIGHT, 'center', mudReport);
    textArrayInBoxes(headersTwo, MM.PRODUCT_LEFT, MM.PRODUCT_TOP, MM.PRODUCT_WIDTH, MM.PRODUCT_HEIGHT, 'center', mudReport);

    mudReport.setFontStyle('normal');
    textArrayInBoxes(productDataOne, MM.PRODUCT_LEFT, MM.PRODUCT_TOP, MM.PRODUCT_WIDTH, MM.PRODUCT_HEIGHT, 'left', mudReport);
    textArrayInBoxes(productDataTwo, MM.PRODUCT_LEFT, MM.PRODUCT_TOP, MM.PRODUCT_WIDTH, MM.PRODUCT_HEIGHT, 'center', mudReport);

};

export const populateEBP = (test: ITest, mudReport: jsPdf) => {
    const ebpHeaderOne: any = [
        ['SECONDARY COSTS'],
        ['Items', undefined, 'Items', undefined],
    ];

    const ebpHeaderTwo: any = [
        [undefined],
        [...Array(2), 'Units', 'Total', ...Array(2), 'Units', 'Total'],
    ];
    
    fillEmptyRows(ebpHeaderOne, MM.EBP_ROWS);
    fillEmptyRows(ebpHeaderTwo, MM.EBP_ROWS);

    const ebpDataOne: any = [
        [undefined],
        [undefined],
    ];

    const ebpDataTwo: any = [
        [undefined],
        [undefined],
    ];

    const headerRows = 2;

    test.ebpInventorys.slice().sort((a, b) => {
        return Number(b.totalCost) - Number(a.totalCost);
    }).forEach((value, index) => {
        if (index < MM.EBP_ROWS - headerRows) {
            ebpDataOne.push([
                value.expenseType.name,
                 ...Array(3)
            ]);
            ebpDataTwo.push([
                ...Array(2),
                value.quantity,
                '$' + Number(value.totalCost).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'),
                ...Array(4),
            ]);
        } else if (index < (MM.EBP_ROWS - headerRows) * 2) {
            ebpDataOne[index + headerRows * 2 - MM.EBP_ROWS][2] = value.expenseType.name;
            ebpDataTwo[index + headerRows * 2 - MM.EBP_ROWS][6] = value.quantity;
            ebpDataTwo[index + headerRows * 2 - MM.EBP_ROWS][7] = '$' + Number(value.totalCost).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        }
    });

    fillEmptyRows(ebpDataOne, MM.EBP_ROWS);
    fillEmptyRows(ebpDataTwo, MM.EBP_ROWS);

    mudReport.setFontStyle('bold');
    textArrayInBoxes(ebpHeaderOne, MM.EBP_LEFT, MM.EBP_TOP, MM.EBP_WIDTH, MM.EBP_HEIGHT, 'center', mudReport);
    textArrayInBoxes(ebpHeaderTwo, MM.EBP_LEFT, MM.EBP_TOP, MM.EBP_WIDTH, MM.EBP_HEIGHT, 'center', mudReport);
    
    mudReport.setFontStyle('normal');
    textArrayInBoxes(ebpDataOne, MM.EBP_LEFT, MM.EBP_TOP, MM.EBP_WIDTH, MM.EBP_HEIGHT, 'center', mudReport);
    textArrayInBoxes(ebpDataTwo, MM.EBP_LEFT, MM.EBP_TOP, MM.EBP_WIDTH, MM.EBP_HEIGHT, 'center', mudReport);


};

export const populateInventoryTotals = (test:ITest, mudReport:jsPdf) => {

    const data: any = [
        ['Daily Cost', '$' + Number(test.inventoryTotal.dailyTotal).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'), 'Cumm Cost', '$' + Number(test.inventoryTotal.cumulativeTotal).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')],
    ];
    mudReport.setFontStyle('bold');
    const tempFontSize = mudReport.internal.getFontSize();
    mudReport.setFontSize(10);
    textArrayInBoxes(data, MM.COST_LEFT, MM.COST_TOP, MM.COST_WIDTH, MM.COST_HEIGHT, 'center', mudReport);
    mudReport.setFontSize(tempFontSize);

};
export const fillEmptyRows = (arrayToFill: any[], length:number) => {
    while(arrayToFill.length < length) {
        arrayToFill.push([undefined]);
    }

    if(arrayToFill.length > length) {
        arrayToFill.splice(length);
    }
};

export const populateEngineer = (well:IWell, test:ITest, mudReport:jsPdf) => {
    const headers: any = [
        ['Engineer', 'Engineer Location', 'Mobile Phone', 'Other Phone', 'Warehouse', 'Warehouse Phone'],
        [undefined]
    ];

    let engineer: IEngineer | undefined;
    well.engineers.forEach(value => {
        if (test.mudReportCreateUser && test.mudReportCreateUser.id === value.id) {
            engineer = value;
        }
    });
    if (!engineer) {
        engineer = new IEngineer();
        if (test.mudReportCreateUser) {
            engineer.firstName = test.mudReportCreateUser.firstName;
            engineer.lastName = test.mudReportCreateUser.lastName;
            engineer.location = test.mudReportCreateUser.location;
            engineer.phoneMobile = test.mudReportCreateUser.phoneMobile;
            engineer.phoneAlternate = test.mudReportCreateUser.phoneMobile;
        }
    }
    const data: any = [
        [undefined],
        [
            engineer.firstName + ' ' + engineer.lastName,
            engineer.location,
            dataMask.phone(engineer.phoneMobile),
            dataMask.phone(engineer.phoneAlternate),
            well.warehouseName,
            dataMask.phone(well.warehousePhone),
        ]
    ];

    mudReport.setFontStyle('bold');
    textArrayInBoxes(headers, MM.PAGE_BORDER_WIDTH, MM.ENGINEER_TOP, MM.ENGINEER_WIDTH, MM.ENGINEER_HEIGHT, 'center', mudReport);
    mudReport.setFontStyle('normal');
    textArrayInBoxes(data, MM.PAGE_BORDER_WIDTH, MM.ENGINEER_TOP, MM.ENGINEER_WIDTH, MM.ENGINEER_HEIGHT, 'center', mudReport);

};

export const populateSolids = (test: ITest, mudReport: jsPdf) => {
    const headers = [
        ['SOLIDS CONTROL'],
        [
            {
                cols: 3,      
                text: 'Device',
            },
            {
                cols: 1,      
                text: 'Act',
            },
            {
                cols: 1,      
                text: 'Qty',
            },
            {
                cols: 1,      
                text: 'Hrs',
            },
        ],
        // 5 + 3 + 5 + 7 + 3
    ];
    fillEmptyRows(headers, MM.SOLIDS_ROWS);
    const data: any = [
        [undefined],
        [undefined],
    ];

    test.solidsControlEquips.forEach(value => {
        data.push([
            {
                cols: 3,      
                text: value.deviceName,
            },
            {
                cols: 1,      
                text: value.inUse ? 'Y' : 'N',
            },
            {
                cols: 1,      
                text: value.quantity,
            },
            {
                cols: 1,      
                text: value.hoursTourly,
            }
        ]);
    });

    fillEmptyRows(data, MM.SOLIDS_ROWS);

    mudReport.setFontStyle('bold');
    textArrayInBoxes(headers, MM.PAGE_BORDER_WIDTH, MM.SOLIDS_TOP, MM.SOLIDS_WIDTH, MM.SOLIDS_HEIGHT, 'center', mudReport);

    mudReport.setFontStyle('normal');
    textArrayInBoxes(data, MM.PAGE_BORDER_WIDTH, MM.SOLIDS_TOP, MM.SOLIDS_WIDTH, MM.SOLIDS_HEIGHT, 'center', mudReport);

};

export const populateFooter = (mudReport: jsPdf) => {
    const text = "The data and recommendations provided herein shall not be construed as authorizing the infringement of any valid patent, " 
    + "and are made without assumption of liability by either the software manufacturer or its author, Mud Company, or its agents," 
    + " and are statements of opinion only. Oral and written statements are opinions only and may be rejected by the user.";

    const splitText = mudReport.splitTextToSize(text, MM.FOOTER_WIDTH - 4);
    mudReport.setFontStyle('normal');
    mudReport.text(splitText, MM.PAGE_BORDER_WIDTH + MM.FOOTER_WIDTH / 2, MM.FOOTER_TOP + 1, {
        align: 'center',
        baseline: 'top',
    });
};

export const contactRepresentatives = (contacts: ITestContact[]) => {
    const allRepresentatives: string[] = [];
    let representatives: string = '';
    if ( contacts ) {
        contacts.map((contact: ITestContact, index: number) => {
            if ( contact.onJob ) {
                const name = contact.firstName + ' ' + contact.lastName;
                allRepresentatives.push(name);
            }
        });
    }
    if ( allRepresentatives.length > 0 ) {
        allRepresentatives.map((representative: string, index: number) => {
            representatives += representative + ', ';
        });
    }
    return representatives.slice(0, -2);
};




// WEBPACK FOOTER //
// ./src/Services/Pdf/MudReport/PopDataMud.ts